import React, { useState, useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Eye, EyeOff, AlertCircle, CheckCircle, Building2, ArrowRight, Loader2 } from 'lucide-react';
import { setAuthToken } from '../utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginPage = () => {
  const { user, login } = useAuth();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState({ isValid: false, errors: [] });
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [signupForm, setSignupForm] = useState({
    company_name: '',
    admin_name: '',
    admin_email: '',
    password: '',
  });

  // Handle impersonation token from platform admin
  useEffect(() => {
    const impersonateToken = searchParams.get('impersonate');
    if (impersonateToken) {
      (async () => {
        try {
          setAuthToken(impersonateToken);
          axios.defaults.headers.common['Authorization'] = `Bearer ${impersonateToken}`;
          const meRes = await axios.get(`${API}/auth/me/permissions`, {
            headers: { Authorization: `Bearer ${impersonateToken}` },
          });
          const perms = meRes.data?.permissions || [];

          const userRes = await axios.get(`${API}/users/me`, {
            headers: { Authorization: `Bearer ${impersonateToken}` },
          });
          const userData = userRes.data;
          const userWithPermissions = { ...userData, permissions: perms };
          localStorage.setItem('user', JSON.stringify(userWithPermissions));
          login(userWithPermissions, impersonateToken);
          toast.success(`Impersonating ${userData.name || userData.email}`);
        } catch (err) {
          toast.error('Impersonation failed — token may be invalid');
          console.error('Impersonation error:', err);
        }
      })();
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchReqs = async () => {
      try {
        const r = await axios.get(`${API}/auth/password-requirements`);
        setPasswordRequirements(r.data);
      } catch {}
    };
    fetchReqs();
  }, []);

  useEffect(() => {
    if (signupForm.password && passwordRequirements) {
      validatePassword(signupForm.password);
    }
  }, [signupForm.password, passwordRequirements]);

  if (user) return <Navigate to="/" replace />;

  const validatePassword = (pw) => {
    if (!passwordRequirements) return;
    const errors = [];
    if (pw.length < passwordRequirements.min_length) errors.push(`At least ${passwordRequirements.min_length} characters`);
    if (passwordRequirements.require_uppercase && !/[A-Z]/.test(pw)) errors.push('One uppercase letter');
    if (passwordRequirements.require_lowercase && !/[a-z]/.test(pw)) errors.push('One lowercase letter');
    if (passwordRequirements.require_numbers && !/[0-9]/.test(pw)) errors.push('One number');
    if (passwordRequirements.require_special_chars && !new RegExp(`[${passwordRequirements.special_chars.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]`).test(pw)) errors.push('One special character');
    setPasswordStrength({ isValid: errors.length === 0, errors });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginForm);
      const { user: userData, token, requires_password_change, org_id } = response.data;

      setAuthToken(token);

      let userPermissions = [];
      try {
        const permRes = await axios.get(`${API}/auth/me/permissions`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        userPermissions = permRes.data?.permissions || [];
      } catch {}

      const userWithPermissions = { ...userData, permissions: userPermissions };
      localStorage.setItem('user', JSON.stringify(userWithPermissions));

      let orgData = null;
      try {
        const orgsRes = await axios.get(`${API}/platform/orgs`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        orgData = (orgsRes.data || []).find((o) => o.org_id === org_id) || null;
      } catch {}

      if (requires_password_change) {
        localStorage.setItem('force_password_change', 'true');
        toast.warning('Password change required!');
        setTimeout(() => { window.location.href = '/profile?force_change=true'; }, 1000);
      } else {
        login(userWithPermissions, token, orgData);
        toast.success('Login successful!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!passwordStrength.isValid) {
      toast.error('Password does not meet requirements');
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/signup`, signupForm);
      const { token, org_id, user: userData, org_name } = response.data;

      setAuthToken(token);

      let userPermissions = [];
      try {
        const permRes = await axios.get(`${API}/auth/me/permissions`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        userPermissions = permRes.data?.permissions || [];
      } catch {}

      const userWithPermissions = { ...userData, permissions: userPermissions };
      localStorage.setItem('user', JSON.stringify(userWithPermissions));

      const orgData = { org_id, name: org_name, role: 'owner' };
      login(userWithPermissions, token, orgData);
      toast.success(`Welcome to Shiftela! "${org_name}" is ready.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setIsLoading(false);
    }
  };

  const PasswordChecklist = ({ password }) => {
    if (!passwordRequirements || !password) return null;
    const checks = [
      { ok: password.length >= passwordRequirements.min_length, label: `${passwordRequirements.min_length}+ characters` },
      { ok: /[A-Z]/.test(password), label: 'Uppercase' },
      { ok: /[a-z]/.test(password), label: 'Lowercase' },
      { ok: /[0-9]/.test(password), label: 'Number' },
      { ok: new RegExp(`[${passwordRequirements.special_chars.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]`).test(password), label: 'Special char' },
    ];
    return (
      <div className="flex flex-wrap gap-2 mt-2">
        {checks.map((c) => (
          <span key={c.label} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${c.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
            {c.ok ? <CheckCircle className="h-3 w-3" /> : <div className="h-3 w-3 rounded-full border border-slate-300" />}
            {c.label}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background accents */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[400px] h-[400px] bg-indigo-600/8 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white tracking-tight" data-testid="app-title">Shiftela</h1>
          <p className="text-slate-400 text-sm mt-1">Workforce Management</p>
        </div>

        <Card className="border-slate-800 bg-slate-900/80 backdrop-blur-xl shadow-2xl">
          <CardHeader className="pb-2">
            <form onSubmit={handleLogin} className="space-y-4 pt-2" data-testid="login-form">
              <div className="space-y-1.5">
                <Label htmlFor="login-email" className="text-slate-300 text-sm">Email</Label>
                <Input
                  id="login-email"
                  data-testid="login-email-input"
                  type="email"
                  placeholder="you@company.com"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  required
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500/20"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="login-password" className="text-slate-300 text-sm">Password</Label>
                <div className="relative">
                  <Input
                    id="login-password"
                    data-testid="login-password-input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    required
                    className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 pr-10 focus:border-blue-500 focus:ring-blue-500/20"
                  />
                  <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff className="h-4 w-4 text-slate-500" /> : <Eye className="h-4 w-4 text-slate-500" />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-500 text-white" disabled={isLoading} data-testid="login-submit-button">
                {isLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Signing in...</> : <>Sign In<ArrowRight className="h-4 w-4 ml-2" /></>}
              </Button>
            </form>
          </CardHeader>
        </Card>

        <p className="text-slate-600 text-xs text-center mt-6">&copy; {new Date().getFullYear()} Shiftela</p>
        <p className="text-slate-700 text-xs text-center mt-1">
          <a href="/platform-login" className="hover:text-slate-500 transition-colors" data-testid="platform-admin-link">
            Platform admin →
          </a>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
